import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Language = 'en' | 'pl' | 'ru';

type Dictionary = Record<string, string>;

const dictionaries: Record<Language, Dictionary> = {
  en: {
    'meta.title': 'Alina Targetologist — Ads Setup for Leads and Sales',
    'meta.description': 'Alina, performance ads specialist: targeted ads setup, audit, strategy and campaign optimization for leads, clients and sales growth.',
    'meta.ogDescription': 'Targeted ads setup and management: audit, strategy, testing and campaign optimization based on data.',
    'meta.twitterDescription': 'Targeted advertising for business: from audit and strategy to leads, clients and scaling.',
    'meta.siteName': 'Alina — Targetologist',
    'meta.imageAlt': 'Alina — targeted advertising specialist',

    'brand.name': 'Alina',
    'brand.role': 'Targetologist',
    'nav.about': 'About',
    'nav.process': 'Process',
    'nav.budget': 'Budget',
    'nav.faq': 'FAQ',
    'nav.adsSetup': 'Ads setup',
    'nav.adBudget': 'Ad budget',
    'nav.sections': 'Sections',
    'nav.contact': 'Contact',

    'hero.topCta': 'Get an audit',
    'hero.label': 'Targeted ads for business',
    'hero.h1.line1': 'Ads setup',
    'hero.h1.line2': 'that pays back.',
    'hero.sub1': 'Targeted ads specialist for business: audit, strategy, launch and campaign optimization.',
    'hero.sub2': 'Not just impressions and clicks, but a system that brings leads, clients and sales.',
    'hero.primaryCta': 'See the process',
    'hero.secondaryCta': 'Get a consultation',
    'hero.note': 'First ad data in 3-7 days',

    'stats.exp': 'years in targeted ads',
    'stats.launches': 'ad launches',
    'stats.days': 'days to first insights',
    'stats.data': 'data-driven work',

    'process.badgeMobile': 'How ads are launched',
    'process.badgeDesktop': 'Ads setup',
    'process.loading': 'Loading',
    'process.aria': 'Targeted advertising setup animation',
    'process.1.title': 'Ad audit',
    'process.1.desc': 'I study your business, product, audience and competitors to find growth points and lead channels.',
    'process.2.title': 'Launch strategy',
    'process.2.desc': 'I choose ad channels, offers, audiences and hypotheses for the first clear test.',
    'process.3.title': 'Creative testing',
    'process.3.desc': 'I launch campaigns and quickly separate working ads from the ones wasting budget.',
    'process.4.title': 'Lead optimization',
    'process.4.desc': 'I cut weak combinations, strengthen effective ones and lower the cost per inquiry.',
    'process.5.title': 'Sales growth',
    'process.5.desc': 'I increase spend where ads already bring leads and can grow without chaos.',

    'timeline.day1': 'Day 1-3',
    'timeline.day2': 'Day 3-7',
    'timeline.day3': 'Day 7+',
    'timeline.1.title': 'First signals',
    'timeline.1.desc': 'Impressions, clicks and first leads appear. You can already see where ads move people and where budget leaks.',
    'timeline.2.title': 'Clear insights',
    'timeline.2.desc': 'We see which audiences, creatives and offers get a response. Weak parts are stopped, working ones are strengthened.',
    'timeline.3.title': 'Lead growth',
    'timeline.3.desc': 'We scale combinations that already perform. Ads become more stable and growth becomes easier to understand.',
    'timeline.badge': 'Launch timing',
    'timeline.title': 'When to expect results',
    'timeline.desc': 'First data appears quickly. Stable leads come after testing and optimization.',

    'services.badge': 'Targetologist services',
    'services.title': 'What is included',
    'services.desc': 'You get more than a one-time ads setup: a full advertising cycle from audit to lead growth.',
    'services.1.title': 'Ad audit',
    'services.1.desc': 'I review the product, audience, competitors and current ads to find lead growth points.',
    'services.2.title': 'Promotion strategy',
    'services.2.desc': 'I build hypotheses, channels, offers and a launch plan that can be tested with data.',
    'services.3.title': 'Campaign launch',
    'services.3.desc': 'I set up and launch ad campaigns with control over budget, creatives and first leads.',
    'services.4.title': 'Ad optimization',
    'services.4.desc': 'I turn off weak combinations, strengthen effective ones and reduce lead cost.',
    'services.5.title': 'Sales scaling',
    'services.5.desc': 'I increase volume where targeted ads already bring leads and clients.',

    'requirements.badge': 'Start without chaos',
    'requirements.title1': 'What is needed',
    'requirements.title2': 'to launch',
    'requirements.desc': 'To set up ads, I need business and product input plus access to ad platforms.',
    'requirements.fromYou': 'FROM YOU',
    'requirements.iTake': 'I HANDLE',
    'requirements.mobileTake': 'I TAKE',
    'requirements.client.1': 'business',
    'requirements.client.2': 'product',
    'requirements.client.3': 'audience',
    'requirements.client.4': 'goals',
    'requirements.my.1': 'audit',
    'requirements.my.2': 'strategy',
    'requirements.my.3': 'launch',
    'requirements.my.4': 'tests',
    'requirements.my.5': 'optimization',
    'requirements.note': 'We clarify the rest on the consultation. The clearer the input, the faster we find audiences, offers and ad combinations.',
    'requirements.imgAlt1': 'Targeted advertising workflow: business, analysis, launch and optimization',
    'requirements.imgAlt2': 'Visual scheme for preparing an advertising launch',

    'budget.badge': 'Ad budget',
    'budget.title1': 'Budget is not a cost,',
    'budget.title2': 'it is a growth tool',
    'budget.desc': 'First we test hypotheses, audiences and offers. Then we invest more where leads appear.',
    'budget.without': 'Without strategy',
    'budget.with.1': 'budget goes into random tests',
    'budget.with.2': 'it is unclear where leads came from',
    'budget.with.3': 'a successful result is hard to repeat',
    'budget.system': 'With an ad system',
    'budget.system.1': 'starter test from $5-10 per day',
    'budget.system.2': 'audiences and offers are clear',
    'budget.system.3': 'you see where leads can grow',
    'budget.factors': 'What affects budget',
    'budget.factor.1': 'target lead cost',
    'budget.factor.2': 'audience size and quality',
    'budget.factor.3': 'competition in your niche',
    'budget.factor.4': 'offer and landing page strength',
    'budget.factor.5': 'region, seasonality and demand',

    'faq.badge': 'Ads FAQ',
    'faq.title1': 'Questions',
    'faq.title2': 'before launch',
    'faq.desc': 'Short answers to the doubts that stop businesses from starting ads.',
    'faq.1.q': 'Do you guarantee leads?',
    'faq.1.a': 'Honest guarantees are impossible without testing. I work with data: test hypotheses, remove weak parts and strengthen ads that generate leads.',
    'faq.2.q': 'When will first data appear?',
    'faq.2.a': 'First ad data usually appears in 3-7 days. Stable leads come after testing, analysis and optimization.',
    'faq.3.q': 'What budget is needed to start?',
    'faq.3.a': 'For first tests, $5-10 per day is often enough. The next budget depends on niche, goal, region and desired lead cost.',
    'faq.4.q': 'Do I need ad experience?',
    'faq.4.a': 'No. I explain ads setup in simple words, show the logic behind decisions and handle the technical work.',
    'faq.5.q': 'What if ads did not work before?',
    'faq.5.a': 'That is a reason to find the cause: where budget leaked, which hypotheses were never tested and what blocked leads.',

    'cta.badge': 'Free first audit',
    'cta.title1': 'Find growth points',
    'cta.title2': 'for your ads',
    'cta.desc': 'We will review your business, audience and first ad hypotheses. You will understand where to start to get leads without wasted spend.',
    'cta.button': 'Get an audit',
    'cta.response': 'I reply within an hour',

    'footer.title': 'Alina — targetologist for business',
    'footer.desc': '2+ years in ads setup. I help businesses get leads and clients through a clear advertising system.',
    'footer.rights': '© 2026 Alina. All rights reserved.',
    'noscript': 'Enable JavaScript to view the website.'
  },
  pl: {
    'meta.title': 'Alina Targetolog — konfiguracja reklam pod leady i sprzedaż',
    'meta.description': 'Alina, specjalistka od reklam: konfiguracja reklam targetowanych, audyt, strategia i optymalizacja kampanii pod leady, klientów i wzrost sprzedaży.',
    'meta.ogDescription': 'Konfiguracja i prowadzenie reklam targetowanych: audyt, strategia, testy i optymalizacja kampanii na podstawie danych.',
    'meta.twitterDescription': 'Reklamy targetowane dla biznesu: od audytu i strategii po leady, klientów i skalowanie.',
    'meta.siteName': 'Alina — Targetolog',
    'meta.imageAlt': 'Alina — specjalistka od reklam targetowanych',

    'brand.name': 'Alina',
    'brand.role': 'Targetolog',
    'nav.about': 'O mnie',
    'nav.process': 'Proces',
    'nav.budget': 'Budżet',
    'nav.faq': 'FAQ',
    'nav.adsSetup': 'Konfiguracja reklam',
    'nav.adBudget': 'Budżet reklamowy',
    'nav.sections': 'Sekcje',
    'nav.contact': 'Kontakt',

    'hero.topCta': 'Otrzymaj audyt',
    'hero.label': 'Reklamy targetowane dla biznesu',
    'hero.h1.line1': 'Konfiguracja',
    'hero.h1.line2': 'reklam z wynikiem.',
    'hero.sub1': 'Targetolog dla biznesu: audyt, strategia, start i optymalizacja kampanii.',
    'hero.sub2': 'Nie tylko wyświetlenia i kliknięcia, ale system, który przynosi leady, klientów i sprzedaż.',
    'hero.primaryCta': 'Zobacz proces',
    'hero.secondaryCta': 'Umów konsultację',
    'hero.note': 'Pierwsze dane reklamowe w 3-7 dni',

    'stats.exp': 'lata w reklamach targetowanych',
    'stats.launches': 'uruchomionych kampanii',
    'stats.days': 'dni do pierwszych wniosków',
    'stats.data': 'praca na danych',

    'process.badgeMobile': 'Jak startują reklamy',
    'process.badgeDesktop': 'Konfiguracja reklam',
    'process.loading': 'Ładowanie',
    'process.aria': 'Animacja konfiguracji reklam targetowanych',
    'process.1.title': 'Audyt reklam',
    'process.1.desc': 'Analizuję biznes, produkt, grupę docelową i konkurencję, aby znaleźć punkty wzrostu i kanały leadów.',
    'process.2.title': 'Strategia startu',
    'process.2.desc': 'Dobieram kanały reklamowe, oferty, grupy odbiorców i hipotezy do pierwszego czytelnego testu.',
    'process.3.title': 'Testowanie kreacji',
    'process.3.desc': 'Uruchamiam kampanie i szybko oddzielam reklamy, które działają, od tych, które przepalają budżet.',
    'process.4.title': 'Optymalizacja leadów',
    'process.4.desc': 'Wyłączam słabe zestawy, wzmacniam skuteczne i obniżam koszt zapytania.',
    'process.5.title': 'Wzrost sprzedaży',
    'process.5.desc': 'Zwiększam budżet tam, gdzie reklama już daje leady i może rosnąć bez chaosu.',

    'timeline.day1': 'Dzień 1-3',
    'timeline.day2': 'Dzień 3-7',
    'timeline.day3': 'Dzień 7+',
    'timeline.1.title': 'Pierwsze sygnały',
    'timeline.1.desc': 'Pojawiają się wyświetlenia, kliknięcia i pierwsze leady. Widać już, dokąd reklama prowadzi odbiorców i gdzie znika budżet.',
    'timeline.2.title': 'Jasne wnioski',
    'timeline.2.desc': 'Widać, które grupy, kreacje i oferty wywołują reakcję. Słabe elementy wyłączamy, działające wzmacniamy.',
    'timeline.3.title': 'Wzrost leadów',
    'timeline.3.desc': 'Skalujemy zestawy, które już działają. Reklama staje się stabilniejsza, a wzrost bardziej przewidywalny.',
    'timeline.badge': 'Terminy startu',
    'timeline.title': 'Kiedy czekać na wynik',
    'timeline.desc': 'Pierwsze dane pojawiają się szybko. Stabilne leady przychodzą po testach i optymalizacji.',

    'services.badge': 'Usługi targetologa',
    'services.title': 'Co obejmuje praca',
    'services.desc': 'Dostajesz nie jednorazową konfigurację reklam, ale pełny cykl pracy: od audytu do wzrostu leadów.',
    'services.1.title': 'Audyt reklam',
    'services.1.desc': 'Sprawdzam produkt, odbiorców, konkurencję i obecne reklamy, aby znaleźć punkty wzrostu leadów.',
    'services.2.title': 'Strategia promocji',
    'services.2.desc': 'Tworzę hipotezy, kanały, oferty i plan startu, który można sprawdzić na danych.',
    'services.3.title': 'Start kampanii',
    'services.3.desc': 'Konfiguruję i uruchamiam kampanie z kontrolą budżetu, kreacji i pierwszych leadów.',
    'services.4.title': 'Optymalizacja reklam',
    'services.4.desc': 'Wyłączam słabe zestawy, wzmacniam skuteczne i obniżam koszt leada.',
    'services.5.title': 'Skalowanie sprzedaży',
    'services.5.desc': 'Zwiększam wolumen tam, gdzie reklamy targetowane już przynoszą leady i klientów.',

    'requirements.badge': 'Start bez chaosu',
    'requirements.title1': 'Co jest potrzebne',
    'requirements.title2': 'do startu',
    'requirements.desc': 'Do konfiguracji reklam potrzebne są informacje o biznesie, produkcie oraz dostęp do platform reklamowych.',
    'requirements.fromYou': 'OD CIEBIE',
    'requirements.iTake': 'JA BIORĘ',
    'requirements.mobileTake': 'JA BIORĘ',
    'requirements.client.1': 'biznes',
    'requirements.client.2': 'produkt',
    'requirements.client.3': 'odbiorcy',
    'requirements.client.4': 'cele',
    'requirements.my.1': 'audyt',
    'requirements.my.2': 'strategię',
    'requirements.my.3': 'start',
    'requirements.my.4': 'testy',
    'requirements.my.5': 'optymalizację',
    'requirements.note': 'Resztę doprecyzujemy na konsultacji. Im jaśniejsze informacje, tym szybciej znajdziemy odbiorców, oferty i zestawy reklam.',
    'requirements.imgAlt1': 'Schemat pracy z reklamami targetowanymi: biznes, analiza, start i optymalizacja',
    'requirements.imgAlt2': 'Wizualny schemat przygotowania startu reklamy',

    'budget.badge': 'Budżet reklamowy',
    'budget.title1': 'Budżet to nie koszt,',
    'budget.title2': 'to narzędzie wzrostu',
    'budget.desc': 'Najpierw testujemy hipotezy, grupy i oferty. Potem inwestujemy więcej tam, gdzie pojawiają się leady.',
    'budget.without': 'Bez strategii',
    'budget.with.1': 'budżet idzie w losowe testy',
    'budget.with.2': 'nie wiadomo, skąd przyszły leady',
    'budget.with.3': 'trudno powtórzyć dobry wynik',
    'budget.system': 'Z systemem reklamowym',
    'budget.system.1': 'test startowy od 5-10$ dziennie',
    'budget.system.2': 'odbiorcy i oferty są jasne',
    'budget.system.3': 'widać, gdzie można rosnąć z leadami',
    'budget.factors': 'Co wpływa na budżet',
    'budget.factor.1': 'docelowy koszt leada',
    'budget.factor.2': 'wielkość i jakość grupy odbiorców',
    'budget.factor.3': 'konkurencja w niszy',
    'budget.factor.4': 'siła oferty i landing page',
    'budget.factor.5': 'region, sezonowość i popyt',

    'faq.badge': 'FAQ reklam',
    'faq.title1': 'Pytania',
    'faq.title2': 'przed startem',
    'faq.desc': 'Krótkie odpowiedzi na wątpliwości, które blokują start reklam.',
    'faq.1.q': 'Czy gwarantujesz leady?',
    'faq.1.a': 'Uczciwe gwarancje bez testów są niemożliwe. Pracuję na danych: testuję hipotezy, usuwam słabe elementy i wzmacniam reklamy, które dają leady.',
    'faq.2.q': 'Kiedy pojawią się pierwsze dane?',
    'faq.2.a': 'Pierwsze dane reklamowe zwykle widać po 3-7 dniach. Stabilne leady pojawiają się po testach, analizie i optymalizacji.',
    'faq.3.q': 'Jaki budżet jest potrzebny na start?',
    'faq.3.a': 'Do pierwszych testów często wystarczy 5-10$ dziennie. Dalszy budżet zależy od niszy, celu, regionu i oczekiwanego kosztu leada.',
    'faq.4.q': 'Czy potrzebuję doświadczenia w reklamach?',
    'faq.4.a': 'Nie. Wyjaśniam konfigurację reklam prostymi słowami, pokazuję logikę decyzji i biorę techniczną część na siebie.',
    'faq.5.q': 'Co jeśli reklamy wcześniej nie zadziałały?',
    'faq.5.a': 'To powód, żeby znaleźć przyczynę: gdzie uciekał budżet, jakich hipotez nie testowano i co blokowało leady.',

    'cta.badge': 'Bezpłatny pierwszy audyt',
    'cta.title1': 'Znajdź punkty wzrostu',
    'cta.title2': 'dla swoich reklam',
    'cta.desc': 'Przejrzymy biznes, odbiorców i pierwsze hipotezy reklamowe. Zrozumiesz, od czego zacząć, aby zdobywać leady bez zbędnych wydatków.',
    'cta.button': 'Otrzymaj audyt',
    'cta.response': 'Odpowiadam w ciągu godziny',

    'footer.title': 'Alina — targetolog dla biznesu',
    'footer.desc': '2+ lata w konfiguracji reklam. Pomagam biznesom zdobywać leady i klientów przez czytelny system reklamowy.',
    'footer.rights': '© 2026 Alina. Wszelkie prawa zastrzeżone.',
    'noscript': 'Włącz JavaScript, aby zobaczyć stronę.'
  },
  ru: {
    'meta.title': 'Таргетолог Алина — настройка рекламы для заявок и продаж',
    'meta.description': 'Таргетолог Алина: настройка таргетированной рекламы, аудит, стратегия и оптимизация кампаний для заявок, клиентов и роста продаж.',
    'meta.ogDescription': 'Запуск и ведение таргетированной рекламы: аудит, стратегия, тесты и оптимизация кампаний по данным.',
    'meta.twitterDescription': 'Настройка таргетированной рекламы для бизнеса: от аудита и стратегии до заявок, клиентов и масштабирования.',
    'meta.siteName': 'Алина — Таргетолог',
    'meta.imageAlt': 'Алина — таргетолог по настройке рекламы',

    'brand.name': 'Алина',
    'brand.role': 'Таргетолог',
    'nav.about': 'Обо мне',
    'nav.process': 'Процесс',
    'nav.budget': 'Бюджет',
    'nav.faq': 'FAQ',
    'nav.adsSetup': 'Настройка рекламы',
    'nav.adBudget': 'Рекламный бюджет',
    'nav.sections': 'Разделы',
    'nav.contact': 'Связаться',

    'hero.topCta': 'Получить разбор',
    'hero.label': 'Таргетированная реклама для бизнеса',
    'hero.h1.line1': 'Реклама',
    'hero.h1.line2': 'которая окупается.',
    'hero.sub1': 'Таргетолог для бизнеса: анализ, стратегия, запуск и оптимизация рекламы.',
    'hero.sub2': 'Не просто показы и клики, а система, которая приводит заявки, клиентов и продажи.',
    'hero.primaryCta': 'Посмотреть процесс',
    'hero.secondaryCta': 'Получить консультацию',
    'hero.note': 'Первые рекламные данные — за 3–7 дней',

    'stats.exp': 'года в таргетированной рекламе',
    'stats.launches': 'рекламных запусков',
    'stats.days': 'дней до первых выводов',
    'stats.data': 'работа по данным',

    'process.badgeMobile': 'Как запускается реклама',
    'process.badgeDesktop': 'Настройка рекламы',
    'process.loading': 'Загрузка',
    'process.aria': 'Анимация процесса настройки таргетированной рекламы',
    'process.1.title': 'Аудит рекламы',
    'process.1.desc': 'Изучаю бизнес, продукт, аудиторию и конкурентов, чтобы найти точки роста и каналы заявок.',
    'process.2.title': 'Стратегия запуска',
    'process.2.desc': 'Подбираю рекламные каналы, офферы, аудитории и гипотезы для первого понятного теста.',
    'process.3.title': 'Тестирование связок',
    'process.3.desc': 'Запускаю кампании и быстро отделяю рабочие объявления от тех, что тратят бюджет.',
    'process.4.title': 'Оптимизация заявок',
    'process.4.desc': 'Убираю слабые связки, усиливаю эффективные и снижаю стоимость обращения.',
    'process.5.title': 'Рост продаж',
    'process.5.desc': 'Увеличиваю бюджет там, где реклама уже приносит заявки и может расти без хаоса.',

    'timeline.day1': 'День 1–3',
    'timeline.day2': 'День 3–7',
    'timeline.day3': 'День 7+',
    'timeline.1.title': 'Первые сигналы',
    'timeline.1.desc': 'Появляются показы, клики и первые заявки. Уже видно, куда реклама ведет аудиторию и где теряется бюджет.',
    'timeline.2.title': 'Понятные выводы',
    'timeline.2.desc': 'Становится видно, какие аудитории, креативы и офферы дают отклик. Лишнее отключаем, рабочее усиливаем.',
    'timeline.3.title': 'Рост заявок',
    'timeline.3.desc': 'Усиливаем связки, которые уже дают результат. Реклама становится стабильнее, а рост понятнее.',
    'timeline.badge': 'Сроки запуска',
    'timeline.title': 'Когда ждать результат',
    'timeline.desc': 'Первые данные появляются быстро. Стабильные заявки приходят после тестирования и оптимизации.',

    'services.badge': 'Услуги таргетолога',
    'services.title': 'Что входит в работу',
    'services.desc': 'Вы получаете не разовую настройку таргета, а полный цикл работы с рекламой: от аудита до роста заявок.',
    'services.1.title': 'Аудит рекламы',
    'services.1.desc': 'Разбираю продукт, аудиторию, конкурентов и текущую рекламу, чтобы найти точки роста заявок.',
    'services.2.title': 'Стратегия продвижения',
    'services.2.desc': 'Формирую гипотезы, каналы, офферы и план запуска, который можно проверить на данных.',
    'services.3.title': 'Запуск кампаний',
    'services.3.desc': 'Настраиваю и запускаю рекламные кампании с контролем бюджета, креативов и первых заявок.',
    'services.4.title': 'Оптимизация рекламы',
    'services.4.desc': 'Отключаю слабые связки, усиливаю эффективные и снижаю стоимость заявки.',
    'services.5.title': 'Масштабирование продаж',
    'services.5.desc': 'Увеличиваю объем там, где таргетированная реклама уже приносит заявки и клиентов.',

    'requirements.badge': 'Старт без хаоса',
    'requirements.title1': 'Что нужно',
    'requirements.title2': 'для запуска',
    'requirements.desc': 'Для настройки рекламы нужны вводные о бизнесе, продукте и доступ к рекламным платформам.',
    'requirements.fromYou': 'ОТ ВАС',
    'requirements.iTake': 'Я БЕРУ НА СЕБЯ',
    'requirements.mobileTake': 'Я БЕРУ',
    'requirements.client.1': 'бизнес',
    'requirements.client.2': 'продукт',
    'requirements.client.3': 'аудитория',
    'requirements.client.4': 'цели',
    'requirements.my.1': 'аудит',
    'requirements.my.2': 'стратегию',
    'requirements.my.3': 'запуск',
    'requirements.my.4': 'тесты',
    'requirements.my.5': 'оптимизацию',
    'requirements.note': 'Остальное уточним на консультации. Чем точнее вводные, тем быстрее находим аудитории, офферы и рекламные связки.',
    'requirements.imgAlt1': 'Схема работы с таргетированной рекламой: бизнес, анализ, запуск и оптимизация',
    'requirements.imgAlt2': 'Визуальная схема подготовки рекламного запуска',

    'budget.badge': 'Рекламный бюджет',
    'budget.title1': 'Бюджет — не расход,',
    'budget.title2': 'а инструмент роста',
    'budget.desc': 'Сначала проверяем гипотезы, аудитории и офферы. Затем вкладываем больше туда, где появляются заявки.',
    'budget.without': 'Без стратегии',
    'budget.with.1': 'бюджет уходит на случайные тесты',
    'budget.with.2': 'неясно, откуда пришли заявки',
    'budget.with.3': 'сложно повторить удачный результат',
    'budget.system': 'С рекламной системой',
    'budget.system.1': 'стартовый тест от 5–10$ в день',
    'budget.system.2': 'понятны аудитории и офферы',
    'budget.system.3': 'видно, где можно растить заявки',
    'budget.factors': 'Что влияет на бюджет',
    'budget.factor.1': 'целевая стоимость заявки',
    'budget.factor.2': 'размер и качество аудитории',
    'budget.factor.3': 'конкуренция в вашей нише',
    'budget.factor.4': 'сила оффера и посадочной страницы',
    'budget.factor.5': 'регион, сезонность и спрос',

    'faq.badge': 'FAQ по рекламе',
    'faq.title1': 'Вопросы',
    'faq.title2': 'перед запуском',
    'faq.desc': 'Коротко отвечаю на сомнения, которые мешают начать настройку рекламы.',
    'faq.1.q': 'Вы гарантируете заявки?',
    'faq.1.a': 'Честные гарантии без тестов невозможны. Я работаю на данных: проверяю гипотезы, убираю слабое и усиливаю рекламу, которая дает заявки.',
    'faq.2.q': 'Когда появятся первые данные?',
    'faq.2.a': 'Первые рекламные данные обычно видны за 3–7 дней. Стабильные заявки появляются после тестирования, анализа и оптимизации.',
    'faq.3.q': 'Какой бюджет нужен для старта?',
    'faq.3.a': 'Для первых тестов часто достаточно 5–10$ в день. Дальше бюджет зависит от ниши, цели, региона и желаемой стоимости заявки.',
    'faq.4.q': 'Нужен ли опыт в рекламе?',
    'faq.4.a': 'Нет. Я объясняю настройку рекламы простыми словами, показываю логику решений и беру техническую часть на себя.',
    'faq.5.q': 'Что если реклама уже не сработала?',
    'faq.5.a': 'Это повод не сдаваться, а найти причину: где сливался бюджет, какие гипотезы не проверяли и что мешало получать заявки.',

    'cta.badge': 'Бесплатный первый разбор',
    'cta.title1': 'Найдем точки роста',
    'cta.title2': 'для вашей рекламы',
    'cta.desc': 'Разберем бизнес, аудиторию и первые рекламные гипотезы. Вы поймете, с чего начать, чтобы получать заявки без лишних трат.',
    'cta.button': 'Получить разбор',
    'cta.response': 'Отвечаю в течение часа',

    'footer.title': 'Алина — таргетолог для бизнеса',
    'footer.desc': '2+ года в настройке рекламы. Помогаю бизнесу получать заявки и клиентов через понятную рекламную систему.',
    'footer.rights': '© 2026 Алина. Все права защищены.',
    'noscript': 'Для просмотра сайта включите JavaScript.'
  }
};

const languages: { code: Language; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'pl', label: 'PL' },
  { code: 'ru', label: 'RU' }
];

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  languages: typeof languages;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function detectBrowserLanguage(): Language {
  const browserLanguage = navigator.language.toLowerCase();
  if (browserLanguage.startsWith('pl')) return 'pl';
  if (browserLanguage.startsWith('ru')) return 'ru';
  return 'en';
}

function setMeta(selector: string, value: string) {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute('content', value);
}

function updateStructuredData(t: (key: string) => string) {
  const url = window.location.origin + window.location.pathname;
  const script = document.head.querySelector<HTMLScriptElement>('script[type="application/ld+json"]');
  if (!script) return;

  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${url}#website`,
        name: t('meta.siteName'),
        url,
        inLanguage: document.documentElement.lang
      },
      {
        '@type': 'Person',
        '@id': `${url}#person`,
        name: t('brand.name'),
        jobTitle: t('brand.role'),
        description: t('meta.description'),
        url,
        sameAs: ['https://www.instagram.com/mysyshyn_target/'],
        knowsAbout: [
          t('hero.label'),
          t('services.1.title'),
          t('services.2.title'),
          t('services.4.title'),
          t('services.5.title')
        ]
      },
      {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: t('meta.title'),
        serviceType: t('hero.label'),
        provider: { '@id': `${url}#person` },
        areaServed: 'Online',
        description: t('meta.ogDescription')
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: [1, 2, 3, 4, 5].map((item) => ({
          '@type': 'Question',
          name: t(`faq.${item}.q`),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t(`faq.${item}.a`)
          }
        }))
      }
    ]
  });
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => detectBrowserLanguage());

  const value = useMemo<I18nContextValue>(() => {
    const t = (key: string) => dictionaries[language][key] ?? dictionaries.en[key] ?? key;
    return { language, setLanguage, t, languages };
  }, [language]);

  useEffect(() => {
    const t = value.t;
    document.documentElement.lang = language;
    document.title = t('meta.title');
    setMeta('meta[name="description"]', t('meta.description'));
    setMeta('meta[property="og:locale"]', language === 'ru' ? 'ru_RU' : language === 'pl' ? 'pl_PL' : 'en_US');
    setMeta('meta[property="og:site_name"]', t('meta.siteName'));
    setMeta('meta[property="og:title"]', t('meta.title'));
    setMeta('meta[property="og:description"]', t('meta.ogDescription'));
    setMeta('meta[property="og:image:alt"]', t('meta.imageAlt'));
    setMeta('meta[name="twitter:title"]', t('meta.title'));
    setMeta('meta[name="twitter:description"]', t('meta.twitterDescription'));
    updateStructuredData(t);
  }, [language, value]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
